// Super-admin gateway console (Payment Gateway v1, Milestone A). Contract §9 pins this
// file's path and says it "imports the dialogs and panels above" — `PayoutApproveDialog`,
// `PayoutRejectDialog` and `TrialBalancePanel` are built elsewhere in `gateway/components/`
// specifically so the Test workstream can exercise them in isolation; this file wires them
// into one page with seven sibling tabs.
//
// Money-moving actions (refund, tenant adjustment, dispute recovery, settings save) are
// NOT individually pinned by the contract, so they are built here directly on the same
// `StepUpConfirmDialog` engine the pinned payout dialogs use — one code path for "confirm,
// then prove it's you, then send it" across all six §4.1-gated endpoints, not six.
//
// Every status is colour + TEXT label. Every amount carries its currency. Zero native
// popups — Radix `Dialog` only.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useT, useLanguage } from "../lib/LanguageContext";
import { SETTLEMENT_CURRENCY } from "../gateway/contract";
import {
  DEFAULT_SCREEN, SECTIONS, screenById, screensIn, sectionOf,
} from "./gateway/nav";
import { NAV_OFF, NAV_ON, RULE_STRONG, SHEET, WIDE_SHEET, ScreenTitle } from "./gateway/Ledger";
import OverviewScreen from "./gateway/OverviewScreen";
import { reconciliationDate } from "./gateway/useOverviewTriage";
import AdjustmentsScreen from "./gateway/AdjustmentsScreen";
import BalancesScreen from "./gateway/BalancesScreen";
import DisputeRatesScreen from "./gateway/DisputeRatesScreen";
import { formatDateTime } from "../lib/dateTime";
import { formatMoneyLedger } from "../lib/formatMoney";
import { formatPercent } from "../lib/formatNumber";
import defaultGatewayApi, { mintIdempotencyKey } from "../gateway/api";
import { extractGatewayErrorCode, extractGatewayErrorFields } from "../gateway/errors";
import { gatewayErrorText } from "../gateway/errorText";
import { Freshness, ResourceState, useGatewayResource } from "./gateway/ResourceState";
import IdCell from "./IdCell";
import { LedgerAmount } from "./LedgerCells";
import PayoutApproveDialog from "../gateway/components/PayoutApproveDialog";
import PayoutRejectDialog from "../gateway/components/PayoutRejectDialog";
import TrialBalancePanel from "../gateway/components/TrialBalancePanel";
import StepUpConfirmDialog from "../gateway/components/StepUpConfirmDialog";
import { useScreenWrites } from "./screenWrites";
// Flow C (Milestone B) — the tenant-facing telehealth timeline component, reused here as
// "the superadmin drill-down" (gateway-flow-c-design.md §10). Path is relative from
// super_admin/ into admin/, same discipline as every other cross-tree gateway import.
import SessionTimeline from "../admin/telehealth/SessionTimeline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, ShieldCheck, Landmark, HelpCircle,
  AlertTriangle, Percent, Settings2, GitCompare, Clock3, ListChecks,
} from "lucide-react";

const PAYMENT_STATE_KEYS = {
  awaiting_payment_method: "gw.state.awaiting_payment_method",
  awaiting_authorisation: "gw.state.awaiting_authorisation",
  card_saved: "gw.state.card_saved",
  authorising: "gw.state.authorising",
  auth_failed: "gw.state.auth_failed",
  requires_capture: "gw.state.requires_capture",
  capturing: "gw.state.capturing",
  captured: "gw.state.captured",
  partially_captured: "gw.state.partially_captured",
  settled: "gw.state.settled",
  cancelling: "gw.state.cancelling",
  cancelled: "gw.state.cancelled",
  expired: "gw.state.expired",
  failed: "gw.state.failed",
  refunded: "gw.state.refunded",
  partially_refunded: "gw.state.partially_refunded",
  disputed: "gw.state.disputed",
};
const PAYMENT_STATE_STYLES = {
  captured: "bg-emerald-50 text-emerald-700",
  settled: "bg-emerald-50 text-emerald-700",
  requires_capture: "bg-blue-50 text-blue-700",
  partially_captured: "bg-blue-50 text-blue-700",
  refunded: "bg-slate-100 text-slate-500",
  partially_refunded: "bg-amber-50 text-amber-700",
  disputed: "bg-rose-50 text-rose-700",
  failed: "bg-rose-50 text-rose-700",
  auth_failed: "bg-rose-50 text-rose-700",
  cancelled: "bg-slate-100 text-slate-400",
  expired: "bg-slate-100 text-slate-400",
};
const DISPUTE_STATE_KEYS = {
  needs_response: "gw.state.needs_response",
  under_review: "gw.state.under_review",
  won: "gw.state.won",
  lost: "gw.state.lost",
};
const DISPUTE_STATE_STYLES = {
  needs_response: "bg-rose-50 text-rose-700",
  under_review: "bg-amber-50 text-amber-700",
  won: "bg-emerald-50 text-emerald-700",
  lost: "bg-slate-100 text-slate-500",
};
// A refund's own lifecycle, written by `modules/gateway/refunds.py`: `authorised` from the
// moment it is created until Stripe answers, then `settled` or `failed`. A separate vocabulary
// from PAYMENT_STATE_KEYS: the key was once built from the raw value (`gw.state.${status}`),
// and `authorised` is no payment state, so the dialog printed the key itself.
const REFUND_STATE_KEYS = {
  authorised: "gw.state.refund.authorised",
  settled: "gw.state.refund.settled",
  failed: "gw.state.refund.failed",
};
const RECOVERY_KEYS = {
  pending: "gw.state.pending",
  netted: "gw.state.netted",
  reversed: "gw.state.reversed",
  absorbed: "gw.state.absorbed",
};
// Payment.productType per contract §2 — not exported as an enum by contract.js because
// it belongs to the checkout body shape, not a state set. Small and stable, so a local
// literal list is honest here rather than a manufactured import.
// MIRRORS backend/modules/gateway/schemas.py:236 `PRODUCT_TYPES`. `telehealth` was missing
// here, so no commission rule could ever be written for a per-minute consultation — the
// flagship Flow C product — and every telehealth capture silently resolved to the platform
// default (commission.resolve_rate's fallback).
const PRODUCT_TYPES = ["telehealth", "in_person", "digital", "physical"];

// `null` is a real, load-bearing value: it is the DEFAULT rule for its scope. The design
// document's resolution order is tenant+type → tenant default → platform+type → platform
// default, first match wins (§7), and without this option the form could only ever write
// the two typed tiers. Kept distinct from "" so an unselected control and a deliberate
// default rule can never be confused.
const ALL_PRODUCT_TYPES = "__all__";

function StateBadge({ state, keys, styles, t, wrap = false }) {
  // `wrap` lets ONE long label stop reserving a column. "Awaiting authorisation" on the
  // payments table forced a 157 px status column out of the ~1015 px a 1440 px laptop
  // has; wrapped it costs the width of its longest word, and that row is already two
  // lines tall. Every other caller keeps `nowrap`, which is right for a one-line row.
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${wrap ? "" : "whitespace-nowrap"} ${styles[state] || "bg-slate-100 text-slate-600"}`}>
      {t(keys[state] || "gw.state.unknown")}
    </span>
  );
}

const TABS = [
  ["queue", "gw.console.tab.queue"],
  ["payments", "gw.console.tab.payments"],
  ["disputes", "gw.console.tab.disputes"],
  ["reconciliation", "gw.console.tab.reconciliation"],
  ["trialBalance", "gw.console.tab.trialBalance"],
  ["commissionRules", "gw.console.tab.commissionRules"],
  ["settings", "gw.console.tab.settings"],
  ["webhooks", "gw.console.tab.webhooks"],
  // Flow C (Milestone B).
  ["consultPricing", "gw.console.tab.consultPricing"],
  ["consults", "gw.console.tab.consults"],
  ["awaitingOperator", "gw.console.tab.awaitingOperator"],
];

// ── Payouts queue ──────────────────────────────────────────────────────────────────
function PayoutsQueueTab({ t, gatewayApi }) {
  const { lang } = useLanguage();
  // ONE fetch-state machine for every list on this console (`ResourceState.jsx`). The
  // hand-rolled `pending / loading / error` this replaced rendered its error only
  // `&& !pending`, so a failed RELOAD kept stale rows on screen with nothing saying so —
  // and this queue had no refresh control at all: it fetched on mount and reloaded only
  // through its own dialog, so a second operator's approval stayed invisible until the
  // section was left and re-entered. The freshness strip is that control.
  const fetcher = useCallback(
    () => gatewayApi.adminPendingPayouts().then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  // Every write on this console is finance.write (the step-up mint too): Read only sees no action.
  const { manage: canAct } = useScreenWrites("gateway");
  const [dialog, setDialog] = useState(null); // {kind: "approve"|"reject", payout}

  const closeDialog = (didMutate) => {
    setDialog(null);
    if (didMutate) resource.reload();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="queue-tab">
      <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100">
        <Freshness resource={resource} id="queue" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-4 py-3">{t("gw.console.payouts.colId")}</th>
            {/* WHOSE money. The queue shipped without this column, so an operator was
                asked to release funds without being told who they belonged to — the
                `tenantId` was on the row the whole time. */}
            <th className="px-4 py-3">{t("gw.console.payouts.colTenant")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.payouts.colAmount")}</th>
            <th className="px-4 py-3">{t("gw.console.payouts.colRequested")}</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <ResourceState resource={resource} id="queue" as="tbody" colSpan={5}
                       emptyText={t("gw.console.payouts.empty")}>
          {(pending) => pending.map((p) => (
            <tr key={p.payoutRequestId} className="border-b border-slate-50 hover:bg-slate-50/50">
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.payoutRequestId}</td>
              {/* The practice, not its primary key. `tenantId` alone answered "whose
                  money is this?" with a slug — see `schemas.practice_ref`. The slug
                  stays, underneath, because it is what every other operator tool and
                  every log line is keyed by. */}
              <td className="px-4 py-3" data-testid={`queue-tenant-${p.payoutRequestId}`}>
                <div className="font-medium text-slate-800">{p.practice?.name || t("gw.console.payouts.unknownPractice")}</div>
                <div className="font-mono text-xs text-slate-500">{p.tenantId || "—"}</div>
                {p.practice?.ownerEmail ? <div className="text-xs text-slate-500">{p.practice.ownerEmail}</div> : null}
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums">
                {formatMoneyLedger(p.amountCents, p.currency, lang)}
                {/* WHAT ACTUALLY LEAVES. The rolling reserve comes out of the payout, so
                    the requested figure is not the transfer. Absent (null) means the row
                    does not state it — never rendered as a zero. */}
                {typeof p.transferAmountCents === "number" && p.transferAmountCents !== p.amountCents ? (
                  <div className="text-xs font-normal text-slate-500" data-testid={`queue-transfer-${p.payoutRequestId}`}>
                    {t("gw.console.payouts.leavesNow")} {formatMoneyLedger(p.transferAmountCents, p.currency, lang)}
                  </div>
                ) : null}
              </td>
              <td className="px-4 py-3 text-slate-500">{formatDateTime(p.requestedAt, lang)}</td>
              <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                {canAct && <>
                <Button size="sm" onClick={() => setDialog({ kind: "approve", payout: p })} data-testid={`queue-approve-${p.payoutRequestId}`}>
                  <ShieldCheck className="w-4 h-4 mr-1" /> {t("gw.console.payouts.approve")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "reject", payout: p })} data-testid={`queue-reject-${p.payoutRequestId}`}>
                  <XCircle className="w-4 h-4 mr-1" /> {t("gw.console.payouts.reject")}
                </Button>
                </>}
              </td>
            </tr>
          ))}
        </ResourceState>
      </table>
      <PayoutApproveDialog payout={dialog?.kind === "approve" ? dialog.payout : null} open={dialog?.kind === "approve"} onClose={closeDialog} gatewayApi={gatewayApi} />
      <PayoutRejectDialog payout={dialog?.kind === "reject" ? dialog.payout : null} open={dialog?.kind === "reject"} onClose={closeDialog} gatewayApi={gatewayApi} />
    </div>
  );
}

// ── Payments list + refund ──────────────────────────────────────────────────────────
function RefundDialog({ payment, open, onClose, gatewayApi, t }) {
  const { lang } = useLanguage();
  const remaining = payment ? payment.amountCapturedCents - (payment.refundedCents || 0) : 0;
  // ONE KEY PER CONFIRMED REFUND, minted when the dialog opens on a payment and stable
  // for every retry of that intent. Without it each attempt minted a fresh refund id
  // server-side, which is also the Stripe idempotency key — so retrying a request whose
  // response was lost sent a SECOND real refund to the customer.
  // THE DEPENDENCIES ARE A CACHE KEY, NOT A READ, and `react-hooks/exhaustive-deps`
  // cannot express that — it asks for `payment` (a fresh object every parent render,
  // which would re-mint the key continuously and reintroduce the double refund above)
  // and then calls `refundedCents` "unnecessary" because the body does not read it. It
  // is necessary: a SECOND partial refund on the same payment is a different intent and
  // must not reuse the first one's key. Suppressed with its reason rather than bent to
  // fit, because both of the rule's suggestions are wrong here. Pre-existing; it fails
  // the CI build, which treats warnings as errors.
  const paymentId = payment?.paymentId;
  const refundedCents = payment?.refundedCents;
  const idempotencyKey = React.useMemo(
    () => (paymentId ? mintIdempotencyKey("ref") : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paymentId, refundedCents]);

  const handleConfirm = async ({ reason, stepUpToken }) => {
    try {
      await gatewayApi.adminRefund({ paymentId: payment.paymentId, amountCents: remaining, reason }, stepUpToken, idempotencyKey);
      toast.success(t("gw.console.payments.refundIssued"));
      return { ok: true };
    } catch (e) {
      return { ok: false, code: extractGatewayErrorCode(e) };
    }
  };

  if (!payment) return null;
  return (
    <StepUpConfirmDialog
      open={open}
      onClose={onClose}
      titleKey="gw.console.payments.refundTitle"
      titleVars={{ id: payment.paymentId }}
      descKey="gw.console.payments.refundDesc"
      requireAck
      ackKey="gw.console.payments.refundAck"
      requireReason
      reasonMinLen={1}
      reasonPlaceholderKey="gw.console.payments.refundReasonPlaceholder"
      onConfirm={handleConfirm}
      gatewayApi={gatewayApi}
    >
      <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-500">{t("gw.console.payments.colRefundable")}</span>
          <span className="font-bold tabular-nums">{formatMoneyLedger(remaining, payment.currency, lang)}</span>
        </div>
      </div>
    </StepUpConfirmDialog>
  );
}

// WHAT WAS SOLD — `schemas.ORDER_KINDS` plus the honest `unknown`, in the order an
// operator scans for them (the practice's own services first, the catalogue after).
// MIRRORS backend/modules/gateway/schemas.py `ORDER_KINDS`; a kind missing here renders
// through `gw.state.unknown` rather than vanishing, but the filter would not offer it.
const PAYMENT_REASONS = ["booking", "telehealth", "course", "pathway", "program", "product",
                         "invoice", "giftcard", "subscription", "unknown"];
const REASON_KEYS = Object.fromEntries(
  PAYMENT_REASONS.map((r) => [r, `gw.console.payments.reason.${r}`]));
// Colour carries MEANING, not decoration: services the practitioner delivers in person or
// on video are one family, catalogue goods another, money owed a third, and `unknown` is
// deliberately the flattest of all — it is the absence of an answer, not a category.
const REASON_STYLES = {
  booking: "bg-sky-50 text-sky-700",
  telehealth: "bg-sky-50 text-sky-700",
  course: "bg-violet-50 text-violet-700",
  pathway: "bg-violet-50 text-violet-700",
  program: "bg-violet-50 text-violet-700",
  product: "bg-amber-50 text-amber-700",
  invoice: "bg-slate-100 text-slate-600",
  giftcard: "bg-emerald-50 text-emerald-700",
  subscription: "bg-indigo-50 text-indigo-700",
  unknown: "bg-slate-50 text-slate-400",
};

const PAYMENTS_PAGE_SIZE = 50;
// The filter names carried in the URL, beside `?screen=payments`. Listed once because
// four separate pieces of code have to agree on them: the reader, the writer, the reset
// and the fetch.
const PAYMENT_FILTER_KEYS = ["tenantId", "reason", "status", "from", "to", "q", "page"];

/** The filter set, read from and written to the URL. */
function usePaymentFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {};
  PAYMENT_FILTER_KEYS.forEach((k) => { filters[k] = params.get(k) || ""; });

  // A FILTERED VIEW IS A LINKABLE VIEW. Operators send each other "look at this
  // practice's gift cards" — and a filter held in component state survives neither the
  // link nor the reload nor the browser's Back button, which on a screen reached by
  // drilling in is the control people actually use.
  const setFilters = (next) => {
    const merged = { screen: "payments" };
    PAYMENT_FILTER_KEYS.forEach((k) => {
      const value = next[k] !== undefined ? next[k] : filters[k];
      if (value) merged[k] = String(value);
    });
    // ANY filter change resets to page 1: page 3 of an unfiltered list is not page 3 of a
    // filtered one, and staying there shows an empty table over a set that has rows.
    if (next.page === undefined) delete merged.page;
    setParams(merged, { replace: false });
  };
  return [filters, setFilters];
}

function PaymentsFilterBar({ filters, setFilters, tenants, total, t }) {
  const [term, setTerm] = useState(filters.q);
  useEffect(() => { setTerm(filters.q); }, [filters.q]);
  const field = "border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white";
  const active = PAYMENT_FILTER_KEYS.some((k) => k !== "page" && filters[k]);

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100"
         data-testid="payments-filters">
      <select className={field} value={filters.tenantId} data-testid="payments-filter-tenant"
              aria-label={t("gw.console.payments.filterTenant")}
              onChange={(e) => setFilters({ tenantId: e.target.value })}>
        <option value="">{t("gw.console.payments.allTenants")}</option>
        {tenants.map((tn) => (
          <option key={tn.tenantId} value={tn.tenantId}>{tn.name || tn.tenantId}</option>
        ))}
      </select>

      <select className={field} value={filters.reason} data-testid="payments-filter-reason"
              aria-label={t("gw.console.payments.filterReason")}
              onChange={(e) => setFilters({ reason: e.target.value })}>
        <option value="">{t("gw.console.payments.allReasons")}</option>
        {PAYMENT_REASONS.map((r) => (
          <option key={r} value={r}>{t(REASON_KEYS[r])}</option>
        ))}
      </select>

      <select className={field} value={filters.status} data-testid="payments-filter-status"
              aria-label={t("gw.console.payments.filterStatus")}
              onChange={(e) => setFilters({ status: e.target.value })}>
        <option value="">{t("gw.console.payments.allStatuses")}</option>
        {Object.keys(PAYMENT_STATE_KEYS).map((s) => (
          <option key={s} value={s}>{t(PAYMENT_STATE_KEYS[s])}</option>
        ))}
      </select>

      <label className="flex items-center gap-1 text-xs text-slate-400">
        {t("gw.console.payments.filterFrom")}
        <input type="date" className={field} value={filters.from} data-testid="payments-filter-from"
               onChange={(e) => setFilters({ from: e.target.value })} />
      </label>
      <label className="flex items-center gap-1 text-xs text-slate-400">
        {t("gw.console.payments.filterTo")}
        <input type="date" className={field} value={filters.to} data-testid="payments-filter-to"
               onChange={(e) => setFilters({ to: e.target.value })} />
      </label>

      {/* A FORM, so Enter submits. The server matches EXACTLY (a payment id, a Stripe
          handle, a full address), so there is nothing useful to show mid-word and every
          keystroke would be a cross-tenant query for a term nobody finished typing. */}
      <form onSubmit={(e) => { e.preventDefault(); setFilters({ q: term.trim() }); }}
            className="flex items-center gap-1">
        <input className={`${field} w-56`} value={term} data-testid="payments-filter-q"
               placeholder={t("gw.console.payments.searchPlaceholder")}
               aria-label={t("gw.console.payments.searchPlaceholder")}
               onChange={(e) => setTerm(e.target.value)} />
        <Button size="sm" variant="outline" type="submit" data-testid="payments-search">
          {t("gw.console.payments.search")}
        </Button>
      </form>

      {active && (
        <button type="button" data-testid="payments-filters-clear"
                className="text-xs text-slate-500 underline underline-offset-4"
                onClick={() => setFilters(Object.fromEntries(
                  PAYMENT_FILTER_KEYS.map((k) => [k, ""])))}>
          {t("gw.console.payments.clearFilters")}
        </button>
      )}
      <span className="ml-auto text-xs text-slate-400 tabular-nums" data-testid="payments-total">
        {t("gw.console.payments.total", { count: total })}
      </span>
    </div>
  );
}

function PaymentDetailDialog({ paymentId, gatewayApi, t, tc, onClose }) {
  const { lang } = useLanguage();
  const [state, setState] = useState({ data: null, error: null, loading: true });

  useEffect(() => {
    if (!paymentId) return undefined;
    let alive = true;
    setState({ data: null, error: null, loading: true });
    gatewayApi.adminGetPayment(paymentId)
      .then((data) => { if (alive) setState({ data, error: null, loading: false }); })
      .catch((e) => {
        if (alive) setState({ data: null, error: extractGatewayErrorCode(e), loading: false });
      });
    return () => { alive = false; };
  }, [paymentId, gatewayApi]);

  const p = state.data;
  const line = (label, value) => (value == null || value === "" ? null : (
    <div className="flex justify-between gap-6 py-1 border-b border-slate-50 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-xs text-slate-800 break-all text-right">{value}</span>
    </div>
  ));

  return (
    <Dialog open={!!paymentId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="payment-detail-dialog">
        <DialogHeader>
          <DialogTitle>{t("gw.console.payments.detailTitle")}</DialogTitle>
        </DialogHeader>
        {state.loading && <p className="text-sm text-slate-400" data-testid="payment-detail-loading"><Loader2 className="w-4 h-4 animate-spin inline" /></p>}
        {!state.loading && state.error && (
          <p className="text-sm text-rose-600" role="alert" data-testid="payment-detail-error">
            {gatewayErrorText(state.error, tc)}
          </p>
        )}
        {!state.loading && p && (
          <div className="text-sm space-y-1" data-testid="payment-detail-body">
            {line(t("gw.console.payments.colId"), p.paymentId)}
            {line(t("gw.console.payments.colTenant"), p.tenantName || p.tenantId)}
            {line(t("gw.console.payments.colReason"), t(REASON_KEYS[p.reason] || "gw.state.unknown"))}
            {line(t("gw.console.payments.colItem"), p.item?.label)}
            {/* THE FULL ADDRESS, and this is the only surface that carries it: the list
                is masked so that browsing every practice's money is not browsing every
                practice's patients. */}
            {line(t("gw.console.payments.colBuyer"), p.customerEmail)}
            {/* The platform's books first (`settled`, settlement currency), then what
                the customer actually paid and the rate Stripe converted at — the same
                split as the list, with nothing converted here. */}
            {line(t("gw.console.payments.colCaptured"), p.settled
              ? formatMoneyLedger(p.settled.capturedCents, p.settled.currency, lang) : null)}
            {line(t("gw.console.payments.colCommission"), p.settled
              ? formatMoneyLedger(p.settled.commissionCents, p.settled.currency, lang) : null)}
            {line(t("gw.console.payments.colNet"), p.settled
              ? formatMoneyLedger(p.settled.netToPracticeCents, p.settled.currency, lang) : null)}
            {line(t("gw.console.payments.detailCustomerPaid"),
                  formatMoneyLedger(p.amountCapturedCents, p.currency, lang))}
            {p.exchangeRate && line(t("gw.console.payments.detailRate"), p.exchangeRate)}
            {line(t("gw.console.payments.detailHoldReleases"), formatDateTime(p.holdReleasesAt, lang))}
            {line("PaymentIntent", p.stripePaymentIntentId)}
            {line("Charge", p.stripeChargeId)}
            {(p.refunds || []).map((r) => (
              <div key={r.refundId} className="flex justify-between gap-6 py-1 text-xs"
                   data-testid={`payment-detail-refund-${r.refundId}`}>
                <span className="text-slate-500">
                  {t(REFUND_STATE_KEYS[r.status] || "gw.state.unknown")} · {formatDateTime(r.createdAt, lang)}
                </span>
                {/* What the customer got back and, when it differs in currency, what the
                    refund takes out of the platform's books (`settledCents`). */}
                <span className="tabular-nums text-right">
                  {formatMoneyLedger(r.amountCents, r.currency, lang)}
                  {r.settledCents != null
                    && String(r.settlementCurrency || "").toLowerCase()
                      !== String(r.currency || "").toLowerCase() && (
                    <span className="block text-slate-400"
                          data-testid={`payment-detail-refund-settled-${r.refundId}`}>
                      {formatMoneyLedger(r.settledCents, r.settlementCurrency, lang)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** One `settled` figure (`schemas.settled_capture`), or a dash when nothing is booked. */
const settledCell = (p, field, lang) => (p.settled
  ? <LedgerAmount cents={p.settled[field]} currency={p.settled.currency} lang={lang} />
  : <span className="text-slate-300">—</span>);

/** The customer paid in another currency than the ledger booked in. */
const chargedElsewhere = (p) => Boolean(p.settled
  && String(p.currency || "").toLowerCase() !== String(p.settled.currency || "").toLowerCase());

function PaymentsTab({ t, tc, gatewayApi }) {
  const { manage: canAct } = useScreenWrites("gateway");
  // THE OPERATOR'S OWN LANGUAGE, sent to the server. A product and a programme are stored
  // as a localised PAIR (`name_fr`/`name_en`), so the item column is only correct if the
  // reader's language travels with the request — without it the server's `en` default
  // printed "Evening tea" to an operator reading the console in French, on a row whose
  // catalogue name is "Tisane du soir". Caught in a real browser, not by a test.
  const { lang } = useLanguage();
  const [filters, setFilters] = usePaymentFilters();
  const page = Math.max(1, parseInt(filters.page, 10) || 1);
  const { tenantId, reason, status, from, to, q } = filters;

  // Params are built from PRIMITIVES, never from the `filters` object: a new object every
  // render would make `fetcher` a new identity every render, and `useGatewayResource`
  // refetches whenever its fetcher changes — an infinite request loop against the
  // operator's busiest read.
  const fetcher = useCallback(() => gatewayApi.adminListPayments({
    ...(tenantId ? { tenantId } : {}),
    ...(reason ? { reason } : {}),
    ...(status ? { status } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(q ? { q } : {}),
    page, pageSize: PAYMENTS_PAGE_SIZE, locale: lang,
  }), [gatewayApi, tenantId, reason, status, from, to, q, page, lang]);
  const resource = useGatewayResource(fetcher);
  const { reload } = resource;
  const rows = resource.data?.rows || [];
  const total = resource.data?.total || 0;
  const rowsResource = { ...resource, data: resource.data ? rows : null };

  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    let alive = true;
    gatewayApi.adminPaymentTenantOptions()
      // Best-effort: a filter that could not load its options must not take the table
      // down with it. The list still works, minus one control.
      .then((d) => { if (alive) setTenants(d.rows || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [gatewayApi]);

  const [refundTarget, setRefundTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const closeRefund = (didMutate) => {
    setRefundTarget(null);
    if (didMutate) reload();
  };

  const shown = rows.length;
  const firstRow = shown ? (page - 1) * PAYMENTS_PAGE_SIZE + 1 : 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="payments-tab">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <span className="text-xs text-slate-400">{t("gw.console.payments.hint")}</span>
        <Freshness resource={resource} id="payments" />
      </div>
      <PaymentsFilterBar filters={filters} setFilters={setFilters} tenants={tenants}
                         total={total} t={t} />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-2 py-3">{t("gw.console.payments.colTenant")}</th>
            <th className="px-2 py-3">{t("gw.console.payments.colReason")}</th>
            <th className="px-2 py-3">{t("gw.console.payments.colItem")}</th>
            <th className="px-2 py-3">{t("gw.console.payments.colBuyer")}</th>
            <th className="px-2 py-3">{t("gw.console.payments.colStatus")}</th>
            <th className="px-2 py-3 text-right whitespace-nowrap">{t("gw.console.payments.colCaptured")}</th>
            <th className="px-2 py-3 text-right whitespace-nowrap">{t("gw.console.payments.colCommission")}</th>
            {/* NOT `whitespace-nowrap`, unlike its two neighbours: "Net to practice" is
                three words and its header, not its figures, was setting the column
                width — 123 px for a column whose widest value needs 89. Wrapping the
                header buys 34 px and loses no meaning; shortening it to "Net" would have
                lost the "to practice", which is the whole distinction from "Captured". */}
            <th className="px-2 py-3 text-right">{t("gw.console.payments.colNet")}</th>
            <th className="px-2 py-3" />
          </tr>
        </thead>
        <ResourceState resource={rowsResource} id="payments" as="tbody" colSpan={9}
                       emptyText={t("gw.console.payments.empty")}>
          {(list) => list.map((p) => {
            const canRefund = (p.amountCapturedCents || 0) - (p.refundedCents || 0) > 0;
            const buyer = p.buyer || {};
            return (
              <tr key={p.paymentId} className="border-b border-slate-50 hover:bg-slate-50/50">
                {/* THE TRUNCATION LIVES INSIDE THE CELL, not on it: `max-width` on a
                    `<td>` is advisory under `table-layout: auto`, so the column still
                    sized itself to the longest practice name and the caps did nothing
                    (measured: the table only lost 66 px when four `max-w` classes were
                    added to cells).

                    THE ID RIDES HERE, as a second line, instead of owning a column. An
                    operator must be able to name what they refunded — the refund modal on
                    `Tenant360` is a blind free-text field — but a 36-character UUID is the
                    least-scanned thing on the row, and its own column cost 138 px of the
                    ~1096 px a 1440 px laptop actually has. Stacked, it costs nothing
                    horizontally and still copies in full. */}
                <td className="px-2 py-2 text-slate-700 align-top">
                  <div className="max-w-[7rem] truncate" title={p.tenantName || p.tenantId}>
                    {p.tenantName || p.tenantId}
                  </div>
                  <IdCell value={p.paymentId} width="max-w-[7rem]" />
                </td>
                <td className="px-2 py-2">
                  <StateBadge state={p.reason} keys={REASON_KEYS} styles={REASON_STYLES} t={t} />
                </td>
                <td className="px-2 py-2">
                  {/* An em-dash, never an invented label: the referent may have been
                      deleted after the sale, and the server says so with `item: null`. */}
                  <div className="max-w-[8rem] truncate"
                       title={p.item
                         ? [p.item.label, p.item.sublabel].filter(Boolean).join(" · ")
                         : undefined}>
                    {p.item ? p.item.label : <span className="text-slate-300">—</span>}
                  </div>
                </td>
                <td className="px-2 py-2 text-slate-600">
                  <div className="max-w-[9rem] truncate"
                       title={buyer.name || buyer.emailMasked || ""}>
                    {buyer.name || buyer.emailMasked || <span className="text-slate-300">—</span>}
                  </div>
                </td>
                <td className="px-2 py-2"><StateBadge state={p.status} keys={PAYMENT_STATE_KEYS} styles={PAYMENT_STATE_STYLES} t={t} wrap /></td>
                {/* `LedgerAmount`: the number never breaks, but a squeezed column wraps
                    BETWEEN the number and its currency code ("1 234 567,89" over "CAD")
                    instead of scrolling the table sideways. A 9-digit capture in fr is 12 px
                    wider than the card at 1280 otherwise. */}
                {/* THE PLATFORM'S BOOKS, NOT THE CUSTOMER'S RECEIPT. The three money
                    columns are `settled` — the capture ledger group's own legs, in the
                    settlement currency (`schemas.settled_capture`). The row's
                    `amountCapturedCents`/`currency` are what the customer was charged; for
                    a practice pricing in another currency they are a different number in
                    a different currency, so they ride as a second line and never in a
                    column the operator sums by eye. No settled group yet → a dash. */}
                <td className="px-2 py-2 text-right tabular-nums"
                    data-testid={`payment-captured-${p.paymentId}`}>
                  {settledCell(p, "capturedCents", lang)}
                  {chargedElsewhere(p) && (
                    <div className="text-[11px] text-slate-400"
                         data-testid={`payment-charged-${p.paymentId}`}>
                      {t("gw.console.payments.charged",
                         { amount: formatMoneyLedger(p.amountCapturedCents, p.currency, lang) })}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-rose-600"
                    data-testid={`payment-commission-${p.paymentId}`}>
                  {settledCell(p, "commissionCents", lang)}
                </td>
                <td className="px-2 py-2 text-right tabular-nums text-emerald-700"
                    data-testid={`payment-net-${p.paymentId}`}>
                  {settledCell(p, "netToPracticeCents", lang)}
                </td>
                {/* LAST, not first, and copyable. The operator has to be able to name
                    what they refunded (`GatewayConsole.paymentsRefund.test.js` pins it,
                    and the refund modal on `Tenant360` is a blind free-text field), but a
                    UUID is not what anyone scans a table by — the practice, the reason and
                    the item are. `IdCell` is the shared primitive both other super-admin
                    ledgers already use for exactly this. */}
                {/* STACKED, not side by side. Two buttons in a row reserved ~150 px of
                    a table that has ~1096 px to live in on a 1440 px laptop; stacked they
                    cost the width of the wider one and the row is already two lines tall
                    because the id sits under the practice. */}
                <td className="px-2 py-2 text-right">
                  <div className="flex flex-col items-end gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                            onClick={() => setDetailId(p.paymentId)}
                            data-testid={`payment-detail-${p.paymentId}`}>
                      {t("gw.console.payments.detail")}
                    </Button>
                    {canAct && canRefund && (
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                              onClick={() => setRefundTarget(p)}
                              data-testid={`refund-${p.paymentId}`}>
                        {t("gw.console.payments.refund")}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </ResourceState>
      </table>

      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
        <span className="text-xs text-slate-400 tabular-nums" data-testid="payments-page-info">
          {t("gw.console.payments.pageInfo",
             { from: firstRow, to: firstRow ? firstRow + shown - 1 : 0, total })}
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1}
                  onClick={() => setFilters({ page: String(page - 1) })}
                  data-testid="payments-prev">{t("gw.console.payments.prev")}</Button>
          <Button size="sm" variant="outline"
                  disabled={page * PAYMENTS_PAGE_SIZE >= total}
                  onClick={() => setFilters({ page: String(page + 1) })}
                  data-testid="payments-next">{t("gw.console.payments.next")}</Button>
        </div>
      </div>

      <RefundDialog payment={refundTarget} open={!!refundTarget} onClose={closeRefund} gatewayApi={gatewayApi} t={t} />
      <PaymentDetailDialog paymentId={detailId} gatewayApi={gatewayApi} t={t} tc={tc}
                           onClose={() => setDetailId(null)} />
    </div>
  );
}

// ── Disputes + recovery ─────────────────────────────────────────────────────────────
function DisputeRecoveryDialog({ dispute, open, onClose, gatewayApi, t }) {
  const { lang } = useLanguage();
  const [mode, setMode] = useState("netted");

  useEffect(() => { if (open) setMode("netted"); }, [open, dispute?.disputeId]);

  const handleConfirm = async ({ stepUpToken }) => {
    try {
      await gatewayApi.adminDisputeRecovery(dispute.disputeId, mode, stepUpToken);
      toast.success(t("gw.console.disputes.recoveryRecorded"));
      return { ok: true };
    } catch (e) {
      return { ok: false, code: extractGatewayErrorCode(e) };
    }
  };

  if (!dispute) return null;
  return (
    <StepUpConfirmDialog
      open={open}
      onClose={onClose}
      titleKey="gw.console.disputes.recoveryTitle"
      titleVars={{ id: dispute.disputeId }}
      descKey="gw.console.disputes.recoveryDesc"
      requireAck
      ackKey="gw.console.disputes.recoveryAck"
      onConfirm={handleConfirm}
      gatewayApi={gatewayApi}
    >
      <div className="space-y-2">
        <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm flex justify-between">
          <span className="text-slate-500">{t("gw.console.disputes.colAmount")}</span>
          <span className="font-bold tabular-nums">{formatMoneyLedger(dispute.amountCents, dispute.currency, lang)}</span>
        </div>
        <div className="flex gap-2">
          {["netted", "absorbed"].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${mode === m ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
              data-testid={`recovery-mode-${m}`}
            >
              {t(RECOVERY_KEYS[m])}
            </button>
          ))}
        </div>
      </div>
    </StepUpConfirmDialog>
  );
}

function DisputesTab({ t, gatewayApi }) {
  const { lang } = useLanguage();
  const { manage: canAct } = useScreenWrites("gateway");
  const fetcher = useCallback(
    () => gatewayApi.adminListDisputes({}).then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const { reload } = resource;
  const [target, setTarget] = useState(null);

  const closeDialog = (didMutate) => {
    setTarget(null);
    if (didMutate) reload();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="disputes-tab">
      <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100">
        <Freshness resource={resource} id="disputes" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-4 py-3">{t("gw.console.disputes.colId")}</th>
            <th className="px-4 py-3">{t("gw.console.disputes.colStatus")}</th>
            <th className="px-4 py-3">{t("gw.console.disputes.colRecovery")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.disputes.colAmount")}</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <ResourceState resource={resource} id="disputes" as="tbody" colSpan={5}
                       emptyText={t("gw.console.disputes.empty")}>
          {(rows) => rows.map((d) => (
            <tr key={d.disputeId} className="border-b border-slate-50 hover:bg-slate-50/50">
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{d.disputeId}</td>
              <td className="px-4 py-3"><StateBadge state={d.status} keys={DISPUTE_STATE_KEYS} styles={DISPUTE_STATE_STYLES} t={t} /></td>
              <td className="px-4 py-3 text-slate-500">{t(RECOVERY_KEYS[d.recovery] || "gw.state.unknown")}</td>
              <td className="px-4 py-3 text-right tabular-nums">{formatMoneyLedger(d.amountCents, d.currency, lang)}</td>
              <td className="px-4 py-3 text-right">
                {canAct && d.recovery === "pending" && (
                  <Button size="sm" variant="outline" onClick={() => setTarget(d)} data-testid={`dispute-recover-${d.disputeId}`}>
                    {t("gw.console.disputes.recover")}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </ResourceState>
      </table>
      <DisputeRecoveryDialog dispute={target} open={!!target} onClose={closeDialog} gatewayApi={gatewayApi} t={t} />
    </div>
  );
}

// ── Reconciliation ──────────────────────────────────────────────────────────────────
function ReconciliationTab({ t, gatewayApi }) {
  const { lang } = useLanguage();
  // YESTERDAY, from the same helper the overview uses. Defaulting to TODAY greeted the
  // operator with "the check has not run yet" even when yesterday's report was present and
  // clean, because `daily_reconcile` runs for the previous day. Two surfaces disagreeing
  // about which day is the day is how a real Stripe mismatch gets ignored.
  const [date, setDate] = useState(() => reconciliationDate());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // TWO CONTROLS, because they are two different things and one of them was pretending
  // to be the other. `load` re-READS the stored report — cheap, and what opening the tab
  // should do. `runNow` asks the backend to COMPUTE one (`?run=true`), which walks a day
  // of Stripe balance transactions and is a deliberate operator action. The panel had a
  // single button labelled "Run reconciliation" that only ever did the first, so on a
  // date the nightly job had not covered it re-read nothing however many times it was
  // pressed — the one control that answers "are the books right now" could not.
  const fetchReport = useCallback(async (run) => {
    setLoading(true);
    try {
      const data = await gatewayApi.adminReconciliation(date, { run });
      setReport(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [gatewayApi, date]);

  const load = useCallback(() => fetchReport(false), [fetchReport]);
  const runNow = useCallback(() => fetchReport(true), [fetchReport]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ABSENCE OF A VERDICT IS NOT A GOOD VERDICT — this file's own thesis, applied to the
  // one panel that was still breaking it. `?? 0` collapsed "the check did not run" into
  // "the check ran and found nothing": `platform_cash_invariant` answers
  // `{driftCents: null, status: "skipped_no_stripe_key"}` under demo mode while
  // `generated` stays true, so the `generated === false` short-circuit below does not
  // fire and the operator was shown a green "no drift" for a comparison nobody made.
  const driftRaw = report?.platformCashInvariant?.driftCents;
  const driftKnown = typeof driftRaw === "number";
  const drift = driftKnown ? driftRaw : 0;
  const driftCurrency = report?.platformCashInvariant?.currency
    || report?.currency || SETTLEMENT_CURRENCY;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="reconciliation-tab">
      <div className="flex items-center gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="max-w-[180px]" data-testid="recon-date" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                data-testid="recon-refresh">
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          {t("gw.console.recon.refresh")}
        </Button>
        <Button size="sm" onClick={runNow} disabled={loading} data-testid="recon-run">
          <GitCompare className="w-4 h-4 mr-1" />
          {t("gw.console.recon.run")}
        </Button>
      </div>
      <p className="text-xs text-slate-500">{t("gw.console.recon.runHint")}</p>

      {error && <p className="text-sm text-rose-600" role="alert">{t("gw.console.loadError")}</p>}

      {/* NOT RUN is not CLEAN. A reconciliation that never happened has no verdict to
          show, and rendering the green "no drift" panel for it — which is what an
          absent `driftCents` would otherwise produce — is the most dangerous thing this
          surface can do: it tells an operator the books were checked and balanced when
          nothing was ever checked. So `generated: false` short-circuits before any
          verdict, in its own neutral state, with the counters withheld rather than
          shown as zeroes. */}
      {report && report.generated === false && (
        <div
          role="status"
          className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm flex items-center gap-2 text-slate-700"
          data-testid="recon-not-run"
        >
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold">{t("gw.console.recon.notRun")}</span>
        </div>
      )}

      {report && report.generated !== false && (
        <>
          <div
            role={driftKnown && drift !== 0 ? "alert" : undefined}
            className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-2 ${
              !driftKnown
                ? "border-slate-300 bg-slate-50 text-slate-700"
                : drift !== 0
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
            data-testid="recon-invariant"
          >
            {!driftKnown
              ? <HelpCircle className="w-5 h-5 flex-shrink-0" />
              : drift !== 0
                ? <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                : <CheckCircle2 className="w-5 h-5 flex-shrink-0" />}
            <span className="font-semibold">
              {!driftKnown
                ? t("gw.console.recon.invariantNotChecked")
                : drift !== 0
                  ? t("gw.console.recon.drift",
                      { amount: formatMoneyLedger(Math.abs(drift), driftCurrency, lang) })
                  : t("gw.console.recon.noDrift")}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-400">{t("gw.console.recon.matched")}</div>
              <div className="font-semibold tabular-nums">{report.matched}</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-400">{t("gw.console.recon.stripeUnmatched")}</div>
              <div className="font-semibold tabular-nums">{(report.stripeUnmatched || []).length}</div>
            </div>
            <div className="rounded-xl bg-slate-50 px-3 py-2">
              <div className="text-xs text-slate-400">{t("gw.console.recon.ledgerUnmatched")}</div>
              <div className="font-semibold tabular-nums">{(report.ledgerUnmatched || []).length}</div>
            </div>
          </div>

          {/* THE FINDINGS. `daily_reconcile` stores two lists this screen never rendered —
              `scheduleFindings` (the platform's payout schedule; since 2026-08-28 also
              `settlement_currency_mismatch`, the platform's Stripe account settling in a
              currency the ledger does not book in, which makes every stripe_fee group
              unbookable and the drift above equal to the day's takings) and `solvency`
              (`SOLVENCY_BREACH`, or `solvency_skipped` when Stripe was not consulted).
              Confirmed live in a browser with the currency finding firing: the only
              channel was the backend ERROR log. Same three-state discipline as the banner:
              a skipped check is NOT CHECKED, never clear; no findings on a computed report
              is an explicit clear line, not silence. */}
          <ReconFindings report={report} t={t} />

          {/* `staleReservationsRepaired` is a DICT, not a list (contract §4): it has to
              separate the one permitted repair from the report-only orphans. Read as a
              list, `.length` is undefined, the comparison is false, and the repair
              signal silently never renders — so count the parts explicitly. */}
          {(() => {
            const s = report.staleReservationsRepaired || {};
            const repaired = (s.released || 0) + (s.rollingReleased || 0) + (s.settled || 0);
            const orphans = (s.orphans || []).length;
            if (!repaired && !orphans) return null;
            return (
              <div className="space-y-1" data-testid="recon-stale">
                {repaired > 0 && (
                  <p className="text-xs text-amber-700">
                    {t("gw.console.recon.staleRepaired", { count: repaired })}
                  </p>
                )}
                {orphans > 0 && (
                  <p className="text-xs text-rose-700" role="alert">
                    {t("gw.console.recon.staleOrphans", { count: orphans })}
                  </p>
                )}
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
}

/** Every stored finding, one line each, keyed by its `check`. Unknown checks still render
 *  (their raw name) — a finding the backend added and the console does not yet name must
 *  not vanish, which is the failure this component replaces. */
function ReconFindings({ report, t }) {
  const { lang } = useLanguage();
  const schedule = report.scheduleFindings || [];
  const solvency = report.solvency || [];
  const all = [...schedule, ...solvency];
  if (all.length === 0) {
    return (
      <p className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="recon-findings-clear">
        <CheckCircle2 className="w-3.5 h-3.5" /> {t("gw.console.recon.findingsClear")}
      </p>
    );
  }
  const line = (f) => {
    switch (f.check) {
      case "settlement_currency_mismatch":
        return t("gw.console.recon.finding.settlementCurrencyMismatch",
                 { account: String(f.accountCurrency || "").toUpperCase(),
                   settlement: String(f.settlementCurrency || "").toUpperCase(),
                   accountId: f.accountId || "" });
      case "platform_payout_schedule_automatic":
        return t("gw.console.recon.finding.payoutScheduleAutomatic", { interval: f.interval || "" });
      case "SOLVENCY_BREACH":
        return t("gw.console.recon.finding.solvencyBreach",
                 { owed: formatMoneyLedger(f.owed_cents, f.currency, lang), available: formatMoneyLedger(f.available_cents, f.currency, lang) });
      case "solvency_skipped":
        return t("gw.console.recon.finding.solvencySkipped");
      default:
        return f.check;
    }
  };
  return (
    <ul className="space-y-1.5" data-testid="recon-findings">
      {all.map((f, i) => {
        const skipped = f.check === "solvency_skipped";
        return (
          <li key={`${f.check}-${i}`}
              role={skipped ? "status" : "alert"}
              className={`text-sm flex items-start gap-2 rounded-xl border px-3 py-2 ${
                skipped ? "border-slate-300 bg-slate-50 text-slate-700"
                        : "border-rose-300 bg-rose-50 text-rose-800"}`}
              data-testid={`recon-finding-${f.check}`}>
            {skipped ? <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                     : <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
            <span>{line(f)}</span>
          </li>
        );
      })}
    </ul>
  );
}

// ── Commission rules (not gated — §4.1 lists only six endpoints, this isn't one) ────
function CommissionRulesTab({ t, tc, gatewayApi }) {
  const { lang } = useLanguage();
  const { manage: canAct } = useScreenWrites("gateway");
  const fetcher = useCallback(
    () => gatewayApi.adminListCommissionRules({}).then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const { reload } = resource;
  const [scope, setScope] = useState("platform");
  const [tenantId, setTenantId] = useState("");
  const [productType, setProductType] = useState(ALL_PRODUCT_TYPES);
  const [rateBps, setRateBps] = useState("2000");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await gatewayApi.adminPutCommissionRule({
        scope,
        tenantId: scope === "tenant" ? tenantId.trim() : null,
        // `__all__` → null: the DEFAULT rule for this scope, not an absent field.
        productType: productType === ALL_PRODUCT_TYPES ? null : productType,
        rateBps: Number(rateBps),
        // NO `effectiveFrom`. It used to be `new Date().toISOString()` — the CLIENT's clock —
        // and the server refuses `effectiveFrom < now` without `force` (commission.upsert_rule),
        // so a browser a second or two slow was refused on a completely ordinary save. Omitting
        // the field hands the decision to `eff = effective_from or now` on the server, where the
        // only clock with authority over the platform's own commission history lives.
      });
      toast.success(t("gw.console.commission.saved"));
      await reload();
    } catch (e) {
      setSaveError(extractGatewayErrorCode(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="commission-rules-tab">
      {canAct && (
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Percent className="w-4 h-4 text-slate-400" /> {t("gw.console.commission.newRule")}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-slate-500">{t("gw.console.commission.scope")}</Label>
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full mt-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm" data-testid="commission-scope">
              <option value="platform">{t("gw.scope.platform")}</option>
              <option value="tenant">{t("gw.scope.tenant")}</option>
            </select>
          </div>
          {scope === "tenant" && (
            <div>
              <Label className="text-xs text-slate-500">{t("gw.console.commission.tenantId")}</Label>
              <Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} data-testid="commission-tenant-id" />
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-500">{t("gw.console.commission.productType")}</Label>
            <select value={productType} onChange={(e) => setProductType(e.target.value)} className="w-full mt-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm" data-testid="commission-product-type">
              <option value={ALL_PRODUCT_TYPES}>{t("gw.productType.all")}</option>
              {PRODUCT_TYPES.map((pt) => <option key={pt} value={pt}>{t(`gw.productType.${pt}`)}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">{t("gw.console.commission.rateBps")}</Label>
            <Input type="number" value={rateBps} onChange={(e) => setRateBps(e.target.value)} data-testid="commission-rate-bps" />
          </div>
        </div>
        {saveError && <p className="text-sm text-rose-600" role="alert">{gatewayErrorText(saveError, tc)}</p>}
        <Button onClick={save} disabled={saving} data-testid="commission-save">
          {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {t("gw.console.commission.save")}
        </Button>
      </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100">
          <Freshness resource={resource} id="commission-rules" />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="px-4 py-3">{t("gw.console.commission.scope")}</th>
              <th className="px-4 py-3">{t("gw.console.commission.tenantId")}</th>
              <th className="px-4 py-3">{t("gw.console.commission.productType")}</th>
              <th className="px-4 py-3 text-right">{t("gw.console.commission.rateBps")}</th>
            </tr>
          </thead>
          <ResourceState resource={resource} id="commission-rules" as="tbody" colSpan={4}
                         emptyText={t("gw.console.commission.empty")}>
            {(rows) => rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-50">
                <td className="px-4 py-3">{t(`gw.scope.${r.scope}`)}</td>
                {/* A deleted practice's rule keeps the address it was negotiated with as
                    `formerTenantId` (its `tenantId` is removed so it resolves for nobody). */}
                <td className="px-4 py-3 text-slate-500" data-testid={`commission-row-${r.id}-tenant`}>
                  {r.tenantId
                    || (r.formerTenantId
                      ? t("gw.console.commission.deletedPractice", { name: r.formerTenantId })
                      : "—")}
                </td>
                {/* A NULL productType is the DEFAULT rule for its scope, not a missing
                    value. Rendering "—" made the tier the form calls "All product types"
                    indistinguishable from the genuinely-not-applicable dash in the tenant
                    column beside it — so an operator could not tell which of the design
                    document's four resolution tiers a row actually was. */}
                <td className="px-4 py-3" data-testid={`commission-row-${r.id}-product`}>{t(`gw.productType.${r.productType || "all"}`)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{formatPercent(r.rateBps / 100, lang)}</td>
              </tr>
            ))}
          </ResourceState>
        </table>
      </div>
    </div>
  );
}

// ── Settings (gated — settings PUT is one of the six §4.1 endpoints) ───────────────
function SettingsTab({ t, tc, gatewayApi }) {
  const { manage: canAct } = useScreenWrites("gateway");
  const fetcher = useCallback(() => gatewayApi.adminGetSettings(), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const settings = resource.data;
  const [draft, setDraft] = useState(null);
  // The draft follows every successful READ — the first one, and the re-read after a
  // save — never a failed one, so a refresh that could not reach the gateway leaves the
  // operator's unsaved edits exactly where they were.
  useEffect(() => { if (resource.data) setDraft(resource.data); }, [resource.data]);
  const [dialogOpen, setDialogOpen] = useState(false);
  // The server refuses with a field pointer per offending setting
  // (`settings_policy.violations` → `detail.fields`). Turning that into a sentence is the
  // CONSOLE's job — the backend deliberately ships a closed `rule` vocabulary and no prose,
  // so without this the operator saw "These settings aren't valid." with two bad fields on
  // screen and nothing saying which.
  const [refused, setRefused] = useState([]);

  // `kind === "nullableNumber"` exists for ONE reason and it is a money reason: a cleared
  // field must send `null`, not `0`. `autoApproveMaxCents` is `int | null`, `null` meaning
  // NO upper bound (§16.5/§16.6 — every eligible request auto-approves). Coercing an empty
  // input through `Number("")` yields 0, which is a $0 ceiling: the exact inverse of the
  // answered policy, silently applied platform-wide by a field the operator only emptied.
  const field = (key, label, kind = "number") => (
    <div key={key}>
      <Label className="text-xs text-slate-500">{label}</Label>
      {kind === "boolean" ? (
        <div className="mt-1.5">
          <Switch checked={!!draft[key]} disabled={!canAct} onCheckedChange={(v) => setDraft((d) => ({ ...d, [key]: v }))} data-testid={`settings-${key}`} />
        </div>
      ) : (
        <Input
          type="number"
          value={draft[key] ?? ""}
          placeholder={kind === "nullableNumber" ? t("gw.console.settings.noCeiling") : undefined}
          readOnly={!canAct}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw === "" && kind === "nullableNumber" ? null : Number(raw);
            setDraft((d) => ({ ...d, [key]: next }));
            setRefused((r) => (r.some((v) => v.field === key)
              ? r.filter((v) => v.field !== key) : r));
          }}
          data-testid={`settings-${key}`}
        />
      )}
      {refused.filter((v) => v.field === key).map((v) => (
        <p key={v.rule} className="text-xs text-rose-600 mt-1" role="alert"
           data-testid={`settings-${key}-error`}>
          {tc(`gw.rule.${v.rule}`, v, tc("gw.rule.invalid"))}
        </p>
      ))}
    </div>
  );

  const handleConfirm = async ({ stepUpToken }) => {
    try {
      const saved = await gatewayApi.adminPutSettings(draft, stepUpToken);
      setDraft(saved);
      // Re-read rather than trust the echo: what the form shows as "saved" is what the
      // gateway will actually apply, and the freshness strip then says when it was read.
      resource.reload();
      setRefused([]);
      toast.success(t("gw.console.settings.saved"));
      return { ok: true };
    } catch (e) {
      setRefused(extractGatewayErrorFields(e));
      return { ok: false, code: extractGatewayErrorCode(e) };
    }
  };

  if (!settings || !draft) {
    // First load, or a hard failure with nothing to show — the shared renderer's states.
    return (
      <div className="bg-white rounded-2xl border border-slate-200" data-testid="settings-tab">
        <ResourceState resource={resource} id="settings" isEmpty={() => false} emptyText="">
          {() => null}
        </ResourceState>
      </div>
    );
  }

  const dirty = JSON.stringify(settings) !== JSON.stringify(draft);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="settings-tab">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Settings2 className="w-4 h-4 text-slate-400" /> {t("gw.console.settings.title")}</h3>
        <Freshness resource={resource} id="settings" />
      </div>
      {/* STALE: the last read failed, and these are the figures from the read before it.
          The operator is about to change platform-wide money policy against them. */}
      {resource.stale && (
        <p className="text-sm text-rose-700" role="alert" data-testid="settings-stale-banner">
          {t("gw.console.state.staleWarning", {
            reason: resource.code ? gatewayErrorText(resource.code, tc) : t("gw.console.state.unreachable") })}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {field("defaultCommissionBps", t("gw.console.settings.defaultCommissionBps"))}
        {field("holdDays", t("gw.console.settings.holdDays"))}
        {field("minPayoutCents", t("gw.console.settings.minPayoutCents"))}
        {field("autoApproveMaxCents", t("gw.console.settings.autoApproveMaxCents"), "nullableNumber")}
        {field("rollingReservePct", t("gw.console.settings.rollingReservePct"))}
        {field("rollingReserveDays", t("gw.console.settings.rollingReserveDays"))}
        {field("noShowFeeCents", t("gw.console.settings.noShowFeeCents"))}
        {field("rescheduleMinNoticeHours", t("gw.console.settings.rescheduleMinNoticeHours"))}
        {field("minRatePerMinuteCents", t("gw.console.settings.minRatePerMinuteCents"))}
        {field("maxRatePerMinuteCents", t("gw.console.settings.maxRatePerMinuteCents"))}
        {field("feesAbsorbedByPlatform", t("gw.console.settings.feesAbsorbedByPlatform"), "boolean")}
        {field("legacyPayoutsDisabled", t("gw.console.settings.legacyPayoutsDisabled"), "boolean")}
      </div>
      {canAct && <>
      <Button onClick={() => setDialogOpen(true)} disabled={!dirty} data-testid="settings-save-open">
        {t("gw.console.settings.save")}
      </Button>

      <StepUpConfirmDialog
        open={dialogOpen}
        onClose={(didMutate) => { setDialogOpen(false); if (!didMutate) setDraft(settings); }}
        titleKey="gw.console.settings.confirmTitle"
        descKey="gw.console.settings.confirmDesc"
        requireAck
        ackKey="gw.console.settings.confirmAck"
        onConfirm={handleConfirm}
        gatewayApi={gatewayApi}
      />
      </>}
    </div>
  );
}

// ── Dead-letter webhooks ─────────────────────────────────────────────────────────────
function WebhooksTab({ t, gatewayApi }) {
  const { lang } = useLanguage();
  const fetcher = useCallback(
    () => gatewayApi.adminDeadLetterWebhooks().then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="webhooks-tab">
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
        <p className="text-xs text-slate-400">{t("gw.console.webhooks.hint")}</p>
        <Freshness resource={resource} id="webhooks" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-4 py-3">{t("gw.console.webhooks.colEvent")}</th>
            <th className="px-4 py-3">{t("gw.console.webhooks.colStatus")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.webhooks.colAttempts")}</th>
            <th className="px-4 py-3">{t("gw.console.webhooks.colReceived")}</th>
            <th className="px-4 py-3">{t("gw.console.webhooks.colProcessed")}</th>
            <th className="px-4 py-3">{t("gw.console.webhooks.colLastError")}</th>
          </tr>
        </thead>
        {/* v1 is READ-ONLY — no requeue endpoint exists, so no retry action here. A
            human reading `lastError` legibly IS the recovery path; it is not one of the
            closed ERROR_CODES, so it is rendered as-is, never mapped through
            gatewayErrorText. Status is likewise an open set (not part of contract §7),
            so it renders as a neutral badge with its own raw text, same precedent as
            StripeTransactions.jsx's unpaid-status cell. */}
        <ResourceState resource={resource} id="webhooks" as="tbody" colSpan={6}
                       emptyText={t("gw.console.webhooks.empty")}>
          {(rows) => rows.map((w) => (
            <tr key={w.eventId} className="border-b border-slate-50">
              <td className="px-4 py-3 font-mono text-xs text-slate-600">{w.type}</td>
              <td className="px-4 py-3">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full" data-testid={`webhook-status-${w.eventId}`}>
                  {w.status}
                </span>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{w.attempts}</td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDateTime(w.receivedAt, lang)}</td>
              <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDateTime(w.processedAt, lang)}</td>
              <td className="px-4 py-3 text-rose-600 max-w-md whitespace-pre-wrap break-words">{w.lastError || "—"}</td>
            </tr>
          ))}
        </ResourceState>
      </table>
    </div>
  );
}

// ── Flow C: pricing approval queue (NOT §4.1-gated — pricing approve/reject is not one
// of the six listed endpoints, so this is a plain confirm dialog, not StepUpConfirmDialog)
function PricingRejectDialog({ row, open, onClose, gatewayApi, t, tc }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => { if (open) { setReason(""); setErrorCode(null); } }, [open]);

  const valid = reason.trim().length >= 5;

  const confirm = async () => {
    setBusy(true);
    setErrorCode(null);
    try {
      await gatewayApi.adminRejectPricing(row.pending.id, reason.trim());
      toast.success(t("gw.console.consultPricing.rejected"));
      onClose(true);
    } catch (e) {
      setErrorCode(extractGatewayErrorCode(e));
    } finally {
      setBusy(false);
    }
  };

  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(false); }}>
      <DialogContent data-testid="consult-pricing-reject-dialog">
        <DialogHeader>
          <DialogTitle>{t("gw.console.consultPricing.rejectTitle")}</DialogTitle>
          <DialogDescription>{t("gw.console.consultPricing.rejectDesc")}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("gw.console.consultPricing.rejectReasonPlaceholder")}
          rows={3}
          data-testid="consult-pricing-reject-reason"
        />
        {errorCode && <p className="text-xs text-rose-600" role="alert">{gatewayErrorText(errorCode, tc)}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={busy}>{t("gw.console.dlg.back")}</Button>
          <Button onClick={confirm} disabled={busy || !valid} data-testid="consult-pricing-reject-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {t("gw.console.consultPricing.rejectConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConsultPricingTab({ t, tc, gatewayApi }) {
  const { lang } = useLanguage();
  const { manage: canAct } = useScreenWrites("gateway");
  const fetcher = useCallback(
    () => gatewayApi.adminPendingPricing().then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const { reload } = resource;
  const [rejectTarget, setRejectTarget] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const approve = async (row) => {
    setApprovingId(row.pending.id);
    try {
      await gatewayApi.adminApprovePricing(row.pending.id);
      toast.success(t("gw.console.consultPricing.approved"));
      reload();
    } catch (e) {
      toast.error(gatewayErrorText(extractGatewayErrorCode(e), tc));
    } finally {
      setApprovingId(null);
    }
  };

  const closeReject = (didMutate) => {
    setRejectTarget(null);
    if (didMutate) reload();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="consult-pricing-tab">
      <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100">
        <Freshness resource={resource} id="consult-pricing" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-4 py-3">{t("gw.console.consultPricing.colTenant")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.consultPricing.colCurrent")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.consultPricing.colProposed")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.consultPricing.colBand")}</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <ResourceState resource={resource} id="consult-pricing" as="tbody" colSpan={5}
                       emptyText={t("gw.console.consultPricing.empty")}>
          {(rows) => rows.map((row) => (
            <tr key={row.pending.id} className="border-b border-slate-50 hover:bg-slate-50/50">
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{row.pending.tenant_id}</td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                {row.current ? formatMoneyLedger(row.current.rate_per_minute_cents, row.current.currency, lang) : "—"}
              </td>
              <td className="px-4 py-3 text-right tabular-nums font-semibold">
                {formatMoneyLedger(row.pending.rate_per_minute_cents, row.pending.currency, lang)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-slate-400 text-xs">
                {formatMoneyLedger(row.band.minRatePerMinuteCents, row.pending.currency, lang)} – {formatMoneyLedger(row.band.maxRatePerMinuteCents, row.pending.currency, lang)}
              </td>
              <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                {canAct && <>
                <Button size="sm" onClick={() => approve(row)} disabled={approvingId === row.pending.id} data-testid={`consult-pricing-approve-${row.pending.id}`}>
                  <ShieldCheck className="w-4 h-4 mr-1" /> {t("gw.console.consultPricing.approve")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setRejectTarget(row)} data-testid={`consult-pricing-reject-${row.pending.id}`}>
                  <XCircle className="w-4 h-4 mr-1" /> {t("gw.console.consultPricing.reject")}
                </Button>
                </>}
              </td>
            </tr>
          ))}
        </ResourceState>
      </table>
      <PricingRejectDialog row={rejectTarget} open={!!rejectTarget} onClose={closeReject} gatewayApi={gatewayApi} t={t} tc={tc} />
    </div>
  );
}

// ── Flow C: consults list + timeline drill-down (SessionTimeline reused "as the
// superadmin drill-down", gateway-flow-c-design.md §10)
function ConsultsTab({ t, tc, gatewayApi }) {
  const { lang } = useLanguage();
  const fetcher = useCallback(
    () => gatewayApi.adminListConsults({}).then((d) => d.rows || []), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const [drillId, setDrillId] = useState(null);
  const [drillSession, setDrillSession] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const openDrill = async (id) => {
    setDrillId(id);
    setDrillLoading(true);
    setDrillError(null);
    try {
      const data = await gatewayApi.adminConsultTimeline(id);
      setDrillSession(data);
    } catch (e) {
      // The timeline IS the dispute answer — "why 52 minutes". A failure here used to set
      // the session to null and say nothing, so the dialog opened on SessionTimeline's
      // "no session" empty state: indistinguishable from a consult that genuinely has no
      // recorded events. That is the difference between "there is no evidence" and "we
      // could not fetch the evidence", and on a disputed charge it is the whole question.
      setDrillSession(null);
      // `{code}`, NEVER the bare code. `extractGatewayErrorCode` answers `null` for
      // everything outside the `{detail:{code}}` envelope — a dropped connection, a 5xx
      // with an HTML body, a proxy failure — and the render guard below tests for the
      // absence of an error. Storing the bare `null` therefore meant "no error" and the
      // dialog fell straight through to SessionTimeline's "no session" empty state: the
      // very "there is no evidence" vs "we could not fetch the evidence" confusion this
      // was written to remove, still live for the whole network-failure class.
      // `StepUpConfirmDialog` already does this correctly on the same data.
      setDrillError({ code: extractGatewayErrorCode(e) });
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="consults-tab">
      <div className="flex items-center justify-end px-4 py-2 border-b border-slate-100">
        <Freshness resource={resource} id="consults" />
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
            <th className="px-4 py-3">{t("gw.console.consults.colId")}</th>
            <th className="px-4 py-3">{t("gw.console.consults.colTenant")}</th>
            <th className="px-4 py-3">{t("gw.console.consults.colStatus")}</th>
            <th className="px-4 py-3">{t("gw.console.consults.colEvidence")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.consults.colMinutes")}</th>
            <th className="px-4 py-3 text-right">{t("gw.console.consults.colAmount")}</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <ResourceState resource={resource} id="consults" as="tbody" colSpan={7}
                       emptyText={t("gw.console.consults.empty")}>
          {(rows) => rows.map((s) => {
            // NEVER RENDERED FROM A GUESSED CURRENCY, same rule as `admin/TelehealthAdmin.jsx`'s
            // consult row: a captured figure is only stateable once the server has ALL THREE of
            // the minutes, the capture, and the currency it was captured in. `billableMinutes`
            // is `null` until the clock finishes, which a bare `??` cannot distinguish from a
            // genuine 0 (a no-show is measured, and its zero is a finding, not an absence).
            const captured = s.billableMinutes != null && s.capturedCents != null && !!s.currency;
            return (
              <tr key={s.sessionId} className="border-b border-slate-50 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.appointmentId}</td>
                <td className="px-4 py-3 text-slate-600">{s.tenantName ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t(`gw.state.session.${s.sessionStatus}`, null, s.sessionStatus)}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{t(`gw.console.consults.evidence.${s.evidenceQuality}`, null, s.evidenceQuality)}</td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid={`consults-minutes-${s.sessionId}`}>
                  {s.billableMinutes != null ? s.billableMinutes : "—"}
                </td>
                <td className="px-4 py-3 text-right tabular-nums" data-testid={`consults-amount-${s.sessionId}`}>
                  {captured ? formatMoneyLedger(s.capturedCents, s.currency, lang) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => openDrill(s.sessionId)} data-testid={`consults-drill-${s.sessionId}`}>
                    <ListChecks className="w-4 h-4 mr-1" /> {t("gw.console.consults.viewTimeline")}
                  </Button>
                </td>
              </tr>
            );
          })}
        </ResourceState>
      </table>

      <Dialog open={!!drillId} onOpenChange={(o) => { if (!o) { setDrillId(null); setDrillSession(null); } }}>
        <DialogContent data-testid="consults-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock3 className="w-4 h-4" /> {t("gw.console.consults.timelineTitle")}</DialogTitle>
          </DialogHeader>
          {drillLoading && <p className="text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></p>}
          {!drillLoading && drillError !== null && (
            <div className="space-y-2" role="alert" data-testid="consult-drill-error">
              <p className="text-sm text-rose-600">{gatewayErrorText(drillError.code, tc)}</p>
              <Button size="sm" variant="outline" onClick={() => openDrill(drillId)}
                      data-testid="consult-drill-retry">
                {t("gw.console.consults.retryTimeline")}
              </Button>
            </div>
          )}
          {!drillLoading && drillError === null && <SessionTimeline session={drillSession} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small shared shape for "open a consult's timeline in a dialog" — identical between
// `ConsultsTab` (above) and `AwaitingOperatorTab` (below), factored once rather than
// copy-pasted a second time.
function useConsultDrill(gatewayApi) {
  const [id, setId] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const open = async (consultId) => {
    setId(consultId);
    setLoading(true);
    setError(null);
    try {
      const data = await gatewayApi.adminConsultTimeline(consultId);
      setSession(data);
    } catch (e) {
      setSession(null);
      setError({ code: extractGatewayErrorCode(e) });
    } finally {
      setLoading(false);
    }
  };
  const close = () => { setId(null); setSession(null); };

  return { id, session, loading, error, open, close };
}

// ── Flow C: the awaiting-operator panel ─────────────────────────────────────────────
//
// A `computed` session is one where the clock ran and the money WAS computed, but the
// capture never landed (`AUTH_EXPIRED`, a decline) — real money sitting invisible until a
// human looks. Before this panel, `grep -r capReachedAt` style silence extended here too:
// nothing on the console rendered this state at all.
//
// Follows the console's own stated design thesis (`ReconciliationTab` above,
// "NOT RUN is not CLEAN"): absence of a verdict is not a good verdict. Three states, never
// collapsed into two —
//   NOT KNOWN     (still loading, or the read failed) — a NEUTRAL colour, never green;
//   VERIFIED CLEAR (loaded, zero rows stuck)          — an explicit green statement;
//   VERIFIED STUCK (loaded, rows present)             — an amber/rose alarm naming them.
// A green "nothing to report" rendered because the fetch simply hadn't resolved yet would
// be the exact defect this panel exists to close, one layer up.
function AwaitingOperatorTab({ t, tc, gatewayApi }) {
  // No `status=` filter exists on `GET /super-admin/consults` (contract §4) — the
  // client-side filter is deliberate, not a workaround for a missing query param the
  // endpoint was ever documented to accept.
  const fetcher = useCallback(
    () => gatewayApi.adminListConsults({})
      .then((d) => (d.rows || []).filter((s) => s.sessionStatus === "computed")),
    [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const { data: rows, loading, error } = resource;
  const drill = useConsultDrill(gatewayApi);

  return (
    <div className="space-y-4" data-testid="awaiting-operator-tab">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-slate-400 max-w-2xl">{t("gw.console.awaitingOperator.hint")}</p>
        <Freshness resource={resource} id="awaiting-operator" />
      </div>

      {/* NOT KNOWN — still loading. Grey, never green. */}
      {loading && rows === null && (
        <div role="status" className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm flex items-center gap-2 text-slate-600" data-testid="awaiting-operator-unknown">
          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          <span>{t("gw.console.awaitingOperator.checking")}</span>
        </div>
      )}

      {/* NOT KNOWN — the read failed. Still never green: a failed check is not a clean one. */}
      {!loading && error && (
        <div role="status" className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 text-sm flex items-center gap-2 text-slate-700" data-testid="awaiting-operator-unknown">
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold">{t("gw.console.awaitingOperator.unknown")}</span>
        </div>
      )}

      {/* VERIFIED CLEAR — loaded, and nothing is stuck. */}
      {!loading && !error && rows && rows.length === 0 && (
        <div role="status" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm flex items-center gap-2 text-emerald-800" data-testid="awaiting-operator-clear">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span className="font-semibold">{t("gw.console.awaitingOperator.clear")}</span>
        </div>
      )}

      {/* VERIFIED STUCK — loaded, and something needs a human. */}
      {!loading && !error && rows && rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 overflow-x-auto" data-testid="awaiting-operator-alert">
          <div role="alert" className="flex items-center gap-2 px-4 py-3 text-sm text-amber-800 border-b border-amber-100 bg-amber-50">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="font-semibold">{t("gw.console.awaitingOperator.stuck", { count: rows.length })}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-4 py-3">{t("gw.console.consults.colId")}</th>
                <th className="px-4 py-3">{t("gw.console.consults.colTenant")}</th>
                <th className="px-4 py-3">{t("gw.console.consults.colEvidence")}</th>
                <th className="px-4 py-3 text-right">{t("gw.console.consults.colMinutes")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.sessionId} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.appointmentId}</td>
                  <td className="px-4 py-3 text-slate-600">{s.tenantName ?? "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{t(`gw.console.consults.evidence.${s.evidenceQuality}`, null, s.evidenceQuality)}</td>
                  <td className="px-4 py-3 text-right tabular-nums">{s.billableMinutes != null ? s.billableMinutes : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => drill.open(s.sessionId)} data-testid={`awaiting-operator-drill-${s.sessionId}`}>
                      <ListChecks className="w-4 h-4 mr-1" /> {t("gw.console.consults.viewTimeline")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!drill.id} onOpenChange={(o) => { if (!o) drill.close(); }}>
        <DialogContent data-testid="awaiting-operator-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock3 className="w-4 h-4" /> {t("gw.console.consults.timelineTitle")}</DialogTitle>
          </DialogHeader>
          {drill.loading && <p className="text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></p>}
          {!drill.loading && drill.error !== null && (
            <div className="space-y-2" role="alert" data-testid="awaiting-operator-drill-error">
              <p className="text-sm text-rose-600">{gatewayErrorText(drill.error.code, tc)}</p>
              <Button size="sm" variant="outline" onClick={() => drill.open(drill.id)}
                      data-testid="awaiting-operator-drill-retry">
                {t("gw.console.consults.retryTimeline")}
              </Button>
            </div>
          )}
          {!drill.loading && drill.error === null && <SessionTimeline session={drill.session} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────────────
export default function GatewayConsole({ gatewayApi = defaultGatewayApi }) {
  const t = useT("superadmin");
  // `gw.error.*` lives in the COMMON namespace (i18n/dictionaries/common/gatewayErrors.js),
  // not in `superadmin`. `gatewayErrorText` takes whatever translator it is handed, and
  // `lookup()` answers the key verbatim on a miss — so passing `t` here printed the literal
  // string "gw.error.COMMISSION_RULE_CONFLICT" to the operator, in all three languages.
  // Threaded separately until the console is split, at which point each screen binds its
  // own and this hazard disappears with the prop.
  const tc = useT("common");
  useLanguage(); // re-render on language switch (the retired PayoutsConsole's convention)

  // THE URL IS THE ONLY STATE. `?screen=payouts` survives a reload and a shared link, and
  // Back walks the operator's own path — overview → payouts → Back returns to the triage
  // list. The SECTION is derived from the screen (`sectionOf`) and never carried in the
  // query, because two fields that can disagree eventually will.
  const [params, setParams] = useSearchParams();
  const raw = params.get("screen") || "";
  const screen = screenById.has(raw) ? raw : null;   // unknown or absent → the overview
  const section = sectionOf(screen);
  const go = useCallback((next) => {
    setParams(next ? { screen: next } : {}, { replace: false });
  }, [setParams]);

  const SCREEN_COMPONENTS = {
    payouts: <PayoutsQueueTab t={t} gatewayApi={gatewayApi} />,
    payments: <PaymentsTab t={t} tc={tc} gatewayApi={gatewayApi} />,
    disputes: <DisputesTab t={t} gatewayApi={gatewayApi} />,
    adjustments: <AdjustmentsScreen t={t} tc={tc} gatewayApi={gatewayApi} />,
    awaitingOperator: <AwaitingOperatorTab t={t} tc={tc} gatewayApi={gatewayApi} />,
    balances: <BalancesScreen t={t} gatewayApi={gatewayApi} />,
    reconciliation: <ReconciliationTab t={t} gatewayApi={gatewayApi} />,
    books: <TrialBalancePanel gatewayApi={gatewayApi} />,
    commission: <CommissionRulesTab t={t} tc={tc} gatewayApi={gatewayApi} />,
    consultRates: <ConsultPricingTab t={t} tc={tc} gatewayApi={gatewayApi} />,
    rules: <SettingsTab t={t} tc={tc} gatewayApi={gatewayApi} />,
    disputeRates: <DisputeRatesScreen t={t} gatewayApi={gatewayApi} />,
    events: <WebhooksTab t={t} gatewayApi={gatewayApi} />,
    consults: <ConsultsTab t={t} tc={tc} gatewayApi={gatewayApi} />,
  };

  return (
    // The payments screen is a wide table, not a document — see `WIDE_SHEET`.
    <div className={screen === "payments" ? WIDE_SHEET : SHEET} data-testid="gateway-console">
      <header className="flex items-baseline justify-between gap-4">
        <button type="button" onClick={() => go(null)}
                className="text-left group" data-testid="console-home">
          <h1 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-slate-900 group-hover:text-slate-600">
            {t("gw.console.title")}
          </h1>
        </button>
      </header>
      <div className={`${RULE_STRONG} mt-2 mb-5`} />

      {/* WRAPS, never scrolls. `overflow-x-auto` hid screens 6-10 behind a scrollbar with no
          affordance — four short section names fit two-up even at 375px. */}
      <nav aria-label={t("gw.console.overview.title")} className="mb-1">
        <div className="flex flex-wrap gap-x-7 gap-y-2 text-sm font-medium">
          {/* The overview is a destination, not just the absence of one. Without an item of
              its own the operator had no positional cue on the landing screen and no
              affordance back except the page title. */}
          <button type="button" onClick={() => go(null)}
                  aria-current={screen === null ? "page" : undefined}
                  className={screen === null ? NAV_ON : NAV_OFF}
                  data-testid="console-section-overview">
            {t("gw.console.section.overview")}
          </button>
          {SECTIONS.map((sec) => (
            <button key={sec} type="button" onClick={() => go(DEFAULT_SCREEN[sec])}
                    aria-current={section === sec ? "page" : undefined}
                    className={section === sec ? NAV_ON : NAV_OFF}
                    data-testid={`console-section-${sec}`}>
              {t(`gw.console.section.${sec}`)}
            </button>
          ))}
        </div>

        {section && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm">
            {screensIn(section).map((sc, i) => (
              <React.Fragment key={sc.key}>
                {i > 0 && <span aria-hidden className="text-slate-300">·</span>}
                <button type="button" onClick={() => go(sc.key)}
                        aria-current={screen === sc.key ? "page" : undefined}
                        className={screen === sc.key
                          ? "text-slate-900 font-medium"
                          : "text-slate-400 hover:text-slate-700"}
                        data-testid={`console-screen-${sc.key}`}>
                  {t(sc.labelKey)}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}
      </nav>

      <div className="mt-6">
        {screen
          ? <>
              <ScreenTitle>{t(screenById.get(screen).labelKey)}</ScreenTitle>
              {SCREEN_COMPONENTS[screen]}
            </>
          : <OverviewScreen gatewayApi={gatewayApi}
                            onGo={go} />}
      </div>
    </div>
  );
}
